import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface RecordingChunk {
  id: string;
  recordingId: string;
  chunkIndex: number;
  blob: Blob;
  createdAt: number;
  sessionId: number;
}

interface RecordingMeta {
  recordingId: string;
  sessionId: number;
  patientId?: number;
  startedAt: number;
  mimeType: string;
  status: 'recording' | 'stopped' | 'uploading' | 'uploaded';
}

interface ScribeRecordingDB extends DBSchema {
  'audio-chunks': {
    key: string;
    value: RecordingChunk;
    indexes: {
      'by-recording': string;
    };
  };
  'recording-meta': {
    key: string;
    value: RecordingMeta;
    indexes: {
      'by-session': number;
    };
  };
}

const DB_NAME = 'scribe-recordings';
const DB_VERSION = 1;
const EXPIRY_MS = 24 * 60 * 60 * 1000;

class RecordingBufferService {
  private dbPromise: Promise<IDBPDatabase<ScribeRecordingDB>> | null = null;

  private getDB(): Promise<IDBPDatabase<ScribeRecordingDB>> {
    if (!this.dbPromise) {
      this.dbPromise = openDB<ScribeRecordingDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          const chunkStore = db.createObjectStore('audio-chunks', { keyPath: 'id' });
          chunkStore.createIndex('by-recording', 'recordingId');

          const metaStore = db.createObjectStore('recording-meta', { keyPath: 'recordingId' });
          metaStore.createIndex('by-session', 'sessionId');
        },
      });
    }
    return this.dbPromise;
  }

  createRecordingId(sessionId: number): string {
    return `rec_${sessionId}_${Date.now()}`;
  }

  async startRecording(meta: RecordingMeta): Promise<void> {
    const db = await this.getDB();
    await db.put('recording-meta', meta);
  }

  async writeChunk(
    recordingId: string,
    chunkIndex: number,
    blob: Blob,
    sessionId: number,
  ): Promise<void> {
    const db = await this.getDB();
    await db.put('audio-chunks', {
      id: `${recordingId}_${chunkIndex}`,
      recordingId,
      chunkIndex,
      blob,
      createdAt: Date.now(),
      sessionId,
    });
  }

  async updateStatus(recordingId: string, status: RecordingMeta['status']): Promise<void> {
    const db = await this.getDB();
    const meta = await db.get('recording-meta', recordingId);
    if (meta) {
      meta.status = status;
      await db.put('recording-meta', meta);
    }
  }

  async getChunks(recordingId: string): Promise<Blob[]> {
    const db = await this.getDB();
    const chunks = await db.getAllFromIndex('audio-chunks', 'by-recording', recordingId);
    chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
    return chunks.map((c) => c.blob);
  }

  async assembleRecording(recordingId: string): Promise<Blob> {
    const db = await this.getDB();
    const meta = await db.get('recording-meta', recordingId);
    const mimeType = meta?.mimeType || 'audio/webm';
    const chunks = await this.getChunks(recordingId);
    return new Blob(chunks, { type: mimeType });
  }

  async deleteRecording(recordingId: string): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(['audio-chunks', 'recording-meta'], 'readwrite');

    const chunkKeys = await tx.objectStore('audio-chunks').index('by-recording').getAllKeys(recordingId);
    for (const key of chunkKeys) {
      await tx.objectStore('audio-chunks').delete(key);
    }

    await tx.objectStore('recording-meta').delete(recordingId);
    await tx.done;
  }

  async getOrphanedRecordings(): Promise<RecordingMeta[]> {
    const db = await this.getDB();
    const allMeta = await db.getAll('recording-meta');
    return allMeta.filter((m) => m.status !== 'uploaded');
  }

  async cleanupExpired(): Promise<number> {
    const db = await this.getDB();
    const allMeta = await db.getAll('recording-meta');
    const cutoff = Date.now() - EXPIRY_MS;
    let cleaned = 0;

    for (const meta of allMeta) {
      if (meta.startedAt < cutoff) {
        await this.deleteRecording(meta.recordingId);
        cleaned++;
      }
    }
    return cleaned;
  }

  async clearAll(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(['audio-chunks', 'recording-meta'], 'readwrite');
    await tx.objectStore('audio-chunks').clear();
    await tx.objectStore('recording-meta').clear();
    await tx.done;
  }

  async requestPersistentStorage(): Promise<boolean> {
    if (navigator.storage && navigator.storage.persist) {
      return navigator.storage.persist();
    }
    return false;
  }
}

export const recordingBufferService = new RecordingBufferService();
export type { RecordingMeta, RecordingChunk };
